import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  LinearProgress,
  Button,
} from '@mui/material';
import {
  FactCheck,
  HowToReg,
  HourglassBottom,
  Description,
  CelebrationOutlined,
} from '@mui/icons-material';
import hrPlusService from '../../services/hrPlusService';
import {
  PageHeader,
  StatCard,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  ErrorState,
  TableEmptyState,
} from '../../components/common/pageKit';
import { useChartTokens } from '../../dashboard/chartTokens';

// ---------------------------------------------------------------------------
// Pure helpers — every section's row shape is not fully specified by the API
// contract, so pick from a list of plausible field names instead of
// assuming one, and never render a missing value as anything but an em dash.
// ---------------------------------------------------------------------------

const pick = (item: any, keys: string[]): any => {
  for (const k of keys) {
    if (item && item[k] !== undefined && item[k] !== null && item[k] !== '') return item[k];
  }
  return undefined;
};

const itemName = (item: any) => pick(item, ['employeeName', 'employee_name', 'name', 'fullName', 'full_name', 'title', 'subject']) ?? '—';
const itemLink = (item: any): string | undefined => pick(item, ['link', 'url', 'href']);

const fmtDate = (v: any) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** Days between today and a date field — positive = upcoming, negative = overdue. */
const daysUntil = (v: any): number | null => {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86400000);
};

const DueChip: React.FC<{ date: any }> = ({ date }) => {
  const days = daysUntil(date);
  if (days === null) return <Typography variant="body2">—</Typography>;
  const color = days < 0 ? 'error.main' : days <= 3 ? 'warning.main' : 'text.primary';
  const label = days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? 'Today' : `${days}d left`;
  return (
    <Typography variant="body2" sx={{ color, fontWeight: days <= 3 ? 700 : 400 }}>
      {label}
    </Typography>
  );
};

interface SectionProps {
  id: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ id, title, subtitle, children }) => (
  <Box id={id} sx={{ mb: 3 }}>
    <Box sx={{ mb: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
    {children}
  </Box>
);

const HrMyDayPage: React.FC = () => {
  const navigate = useNavigate();
  const T = useChartTokens();

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    hrPlusService
      .myDay()
      .then((res: any) => setData(res))
      .catch((e: any) => setError(e?.message || 'Failed to load your HR day.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const approvals: any[] = data?.approvals || [];
  const onboardingDue: any[] = data?.onboardingDue || [];
  const probationsDue: any[] = data?.probationsDue || [];
  const documentsExpiring: any[] = data?.documentsExpiring || [];

  const totalCount = approvals.length + onboardingDue.length + probationsDue.length + documentsExpiring.length;

  const goto = (item: any) => {
    const link = itemLink(item);
    if (link) navigate(link);
  };

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const stats = useMemo(
    () => [
      { key: 'approvals', title: 'Pending Approvals', value: approvals.length, icon: <FactCheck />, color: T.categorical[0] },
      { key: 'onboarding', title: 'Onboarding Tasks Due', value: onboardingDue.length, icon: <HowToReg />, color: T.categorical[1] },
      { key: 'probations', title: 'Probations Completing', value: probationsDue.length, icon: <HourglassBottom />, color: T.categorical[2] },
      { key: 'documents', title: 'Documents Expiring', value: documentsExpiring.length, icon: <Description />, color: T.categorical[3] },
    ],
    [approvals, onboardingDue, probationsDue, documentsExpiring, T]
  );

  return (
    <Box>
      <PageHeader title="My HR Day" subtitle="Pending approvals, onboarding, probations and expiring documents — everything on your plate today" />

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {error && !data ? (
        <ErrorState message={error} onRetry={load} />
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {stats.map((s) => (
              <Grid key={s.key} size={{ xs: 6, sm: 6, md: 3 }}>
                <StatCard title={s.title} value={s.value} icon={s.icon} color={s.color} onClick={() => scrollTo(s.key)} />
              </Grid>
            ))}
          </Grid>

          {!loading && totalCount === 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                py: 6,
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 3,
                mb: 3,
              }}
            >
              <CelebrationOutlined sx={{ fontSize: 32, color: 'success.main' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                All clear
              </Typography>
              <Typography variant="caption" color="text.secondary">
                No pending approvals, onboarding tasks, probations or expiring documents right now.
              </Typography>
            </Box>
          )}

          <Section id="approvals" title="Pending Approvals" subtitle="Leave, regularization and other requests awaiting your decision">
            <TableSurface>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Employee</TableCell>
                    <TableCell sx={headerCellSx}>Type</TableCell>
                    <TableCell sx={headerCellSx}>Requested On</TableCell>
                    <TableCell sx={headerCellSx} align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {approvals.map((a: any, i: number) => (
                    <TableRow key={a.id ?? i} sx={bodyRowSx}>
                      <TableCell>{itemName(a)}</TableCell>
                      <TableCell>{pick(a, ['type', 'requestType', 'request_type', 'category']) ?? '—'}</TableCell>
                      <TableCell>{fmtDate(pick(a, ['requestedOn', 'requested_on', 'createdAt', 'created_at', 'date']))}</TableCell>
                      <TableCell align="right">
                        <Button size="small" disabled={!itemLink(a)} onClick={() => goto(a)}>
                          Review
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {!loading && !approvals.length && <TableEmptyState title="No approvals pending" hint="Nothing waiting on your decision." />}
            </TableSurface>
          </Section>

          <Section id="onboarding" title="Onboarding Tasks Due" subtitle="New-joiner checklist items coming due">
            <TableSurface>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Employee</TableCell>
                    <TableCell sx={headerCellSx}>Task</TableCell>
                    <TableCell sx={headerCellSx}>Due Date</TableCell>
                    <TableCell sx={headerCellSx}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {onboardingDue.map((o: any, i: number) => {
                    const due = pick(o, ['dueDate', 'due_date', 'targetDate', 'target_date']);
                    return (
                      <TableRow key={o.id ?? i} sx={itemLink(o) ? { ...bodyRowSx, cursor: 'pointer' } : bodyRowSx} onClick={() => goto(o)}>
                        <TableCell>{itemName(o)}</TableCell>
                        <TableCell>{pick(o, ['task', 'taskName', 'task_name', 'title']) ?? '—'}</TableCell>
                        <TableCell>{fmtDate(due)}</TableCell>
                        <TableCell>
                          <DueChip date={due} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {!loading && !onboardingDue.length && <TableEmptyState title="No onboarding tasks due" hint="Every new joiner is on track." />}
            </TableSurface>
          </Section>

          <Section id="probations" title="Probations Completing" subtitle="Employees whose probation period is ending soon">
            <TableSurface>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Employee</TableCell>
                    <TableCell sx={headerCellSx}>Department</TableCell>
                    <TableCell sx={headerCellSx}>Probation End Date</TableCell>
                    <TableCell sx={headerCellSx}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {probationsDue.map((p: any, i: number) => {
                    const end = pick(p, ['probationEndDate', 'probation_end_date', 'endDate', 'end_date', 'dueDate', 'due_date']);
                    return (
                      <TableRow key={p.id ?? i} sx={itemLink(p) ? { ...bodyRowSx, cursor: 'pointer' } : bodyRowSx} onClick={() => goto(p)}>
                        <TableCell>{itemName(p)}</TableCell>
                        <TableCell>{pick(p, ['department', 'departmentName', 'department_name', 'designation']) ?? '—'}</TableCell>
                        <TableCell>{fmtDate(end)}</TableCell>
                        <TableCell>
                          <DueChip date={end} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {!loading && !probationsDue.length && <TableEmptyState title="No probations completing" hint="Nothing due for review this period." />}
            </TableSurface>
          </Section>

          <Section id="documents" title="Documents Expiring" subtitle="Employee documents approaching their expiry date">
            <TableSurface>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Employee</TableCell>
                    <TableCell sx={headerCellSx}>Document Type</TableCell>
                    <TableCell sx={headerCellSx}>Expiry Date</TableCell>
                    <TableCell sx={headerCellSx}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documentsExpiring.map((d: any, i: number) => {
                    const expiry = pick(d, ['expiryDate', 'expiry_date', 'dueDate', 'due_date']);
                    return (
                      <TableRow key={d.id ?? i} sx={itemLink(d) ? { ...bodyRowSx, cursor: 'pointer' } : bodyRowSx} onClick={() => goto(d)}>
                        <TableCell>{itemName(d)}</TableCell>
                        <TableCell>{pick(d, ['documentType', 'document_type', 'type']) ?? '—'}</TableCell>
                        <TableCell>{fmtDate(expiry)}</TableCell>
                        <TableCell>
                          <DueChip date={expiry} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {!loading && !documentsExpiring.length && <TableEmptyState title="No documents expiring" hint="Nothing needs renewal right now." />}
            </TableSurface>
          </Section>
        </>
      )}
    </Box>
  );
};

export default HrMyDayPage;
